/**
 * Shared trip device-sync pull via canonicalTripId (Phase 2 / Phase 4 web).
 */

import { API_BASE_URL, buildAuthHeaders } from './apiClient';
import type { TripMembership } from './tripShareService';

export const SHARED_OVERLAY_KEYS = ['accessRole', 'canEdit', 'canonicalTripId'] as const;

export function parseCanonicalTripId(
  canonicalTripId: string
): { authorSub: string; tripId: string } | null {
  const parts = canonicalTripId.split('#');
  if (parts.length !== 3 || parts[1] !== 'trip' || !parts[0] || !parts[2]) return null;
  return { authorSub: parts[0], tripId: parts[2] };
}

export function stripOverlays<T extends Record<string, unknown>>(body: T): T {
  const copy = { ...body };
  for (const key of SHARED_OVERLAY_KEYS) {
    delete copy[key];
  }
  return copy;
}

export function overlayValues(body: Record<string, unknown>): {
  accessRole: string | null;
  canEdit: boolean | null;
  canonicalTripId: string | null;
} {
  const roleRaw = body.accessRole != null ? String(body.accessRole).trim().toLowerCase() : '';
  return {
    accessRole: roleRaw || null,
    canEdit: typeof body.canEdit === 'boolean' ? body.canEdit : null,
    canonicalTripId:
      body.canonicalTripId != null && String(body.canonicalTripId).trim()
        ? String(body.canonicalTripId).trim()
        : null,
  };
}

export function lastSyncStorageKey(canonicalTripId: string): string {
  return `sharedTripSync.lastSyncAt.${canonicalTripId}`;
}

export function authorPrefixedTripId(authorSub: string, tripId: string): string {
  return `${authorSub}#trip#${tripId.toLowerCase()}`;
}

export type DeviceSyncChangeRecord = {
  id: string;
  type: string;
  body: Record<string, unknown>;
  updatedAt?: string | null;
};

export async function fetchSharedTripChanges(
  canonicalTripId: string,
  since?: string | null
): Promise<DeviceSyncChangeRecord[]> {
  const headers = await buildAuthHeaders();
  const all: DeviceSyncChangeRecord[] = [];
  let cursor: string | null = null;
  do {
    const params = new URLSearchParams({
      limit: '100',
      canonicalTripId,
    });
    if (since) params.set('since', since);
    if (cursor) params.set('cursor', cursor);
    const res = await fetch(`${API_BASE_URL}/devicesync/changes?${params}`, {
      method: 'GET',
      headers,
    });
    if (res.status === 403) {
      throw new Error('SHARED_TRIP_FORBIDDEN');
    }
    if (!res.ok) {
      throw new Error(`Shared sync failed (${res.status})`);
    }
    const json = (await res.json()) as { records?: DeviceSyncChangeRecord[] };
    all.push(...(json.records ?? []));
    cursor = res.headers.get('Next-Cursor')?.trim() || null;
  } while (cursor);
  return all;
}

/** Apply overlays from trip-v1 records onto a local trip-shaped object. */
export function applyOverlaysToTripDraft(
  draft: Record<string, unknown>,
  records: DeviceSyncChangeRecord[],
  fallbackCanonical: string
): Record<string, unknown> {
  const tripRecords = records.filter((r) => r.type === 'trip-v1');
  const latest = tripRecords[tripRecords.length - 1];
  const next = { ...draft };
  next.canonicalTripId = fallbackCanonical;
  if (!latest) return next;
  const overlays = overlayValues(latest.body ?? {});
  if (overlays.canonicalTripId) next.canonicalTripId = overlays.canonicalTripId;
  if (overlays.accessRole) next.accessRole = overlays.accessRole;
  if (overlays.canEdit != null) next.canEdit = overlays.canEdit;
  else if (overlays.accessRole === 'viewer') next.canEdit = false;
  return next;
}

export async function pullActiveSharedMemberships(
  memberships: TripMembership[]
): Promise<{ canonicalTripId: string; records: DeviceSyncChangeRecord[] }[]> {
  const results: { canonicalTripId: string; records: DeviceSyncChangeRecord[] }[] = [];
  for (const m of memberships) {
    if (m.status !== 'active' || !m.canonicalTripId) continue;
    if (!parseCanonicalTripId(m.canonicalTripId)) continue;
    const since = localStorage.getItem(lastSyncStorageKey(m.canonicalTripId));
    try {
      const records = await fetchSharedTripChanges(m.canonicalTripId, since);
      results.push({ canonicalTripId: m.canonicalTripId, records });
      const maxUpdated = records
        .map((r) => r.updatedAt)
        .filter(Boolean)
        .sort()
        .at(-1);
      if (maxUpdated) {
        localStorage.setItem(lastSyncStorageKey(m.canonicalTripId), maxUpdated);
      }
    } catch (e) {
      if (e instanceof Error && e.message === 'SHARED_TRIP_FORBIDDEN') {
        localStorage.removeItem(lastSyncStorageKey(m.canonicalTripId));
      }
      throw e;
    }
  }
  return results;
}
